import { useEffect, useState } from "react";
import { marketplaceDB } from "@/lib/db";
import { ShoppingBag, Download, Star, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { GridSkeleton } from "@/components/Skeleton";
import { notifyError } from "@/lib/errorToast";

type Item = {
  id: string;
  kind: string;
  name: string;
  description: string | null;
  installs: number;
  rating: number | null;
  payload: unknown;
  author_id: string;
};

export function Marketplace() {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [installingId, setInstallingId] = useState<string | null>(null);

  useEffect(() => {
    refresh();
  }, []);

  async function refresh() {
    setLoading(true);
    try {
      const data = await marketplaceDB.list();
      setItems(data as Item[]);
    } catch (e) {
      notifyError(e as Error, "Falha ao carregar marketplace");
      setItems([]);
    }
    setLoading(false);
  }

  async function install(it: Item) {
    setInstallingId(it.id);
    try {
      await marketplaceDB.install(it.id);
      toast.success(`${it.name} instalado`);
      refresh();
    } catch (e) {
      notifyError(e as Error, "Falha ao instalar");
    }
    setInstallingId(null);
  }

  return (
    <div className="h-full overflow-auto p-6 space-y-4">
      <div>
        <h2 className="text-2xl font-display font-bold flex items-center gap-2">
          <ShoppingBag className="w-6 h-6 text-primary" /> Marketplace
        </h2>
        <p className="text-sm text-muted-foreground">
          Browse and install community agents, workflows and tools — direto do banco
        </p>
      </div>

      {loading ? (
        <GridSkeleton count={6} />
      ) : items.length === 0 ? (
        <div className="glass-panel p-12 rounded-lg border border-primary/20 text-center space-y-3">
          <ShoppingBag className="w-12 h-12 mx-auto text-muted-foreground opacity-40" />
          <p className="text-sm text-muted-foreground">Nenhum item publicado ainda.</p>
          <p className="text-xs text-muted-foreground">
            Vá ao <strong>Builder Studio</strong>, crie um agente e clique em <em>Publish to Marketplace</em>.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((it) => (
            <div key={it.id} className="glass-panel p-5 rounded-lg border border-primary/20 flex flex-col">
              <div className="flex items-start justify-between mb-2">
                <span className="text-[10px] font-mono uppercase text-primary bg-primary/10 px-2 py-0.5 rounded">{it.kind}</span>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {(it.rating ?? 4.7).toFixed(1)}
                </div>
              </div>
              <h3 className="font-semibold mb-1 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-accent" />
                {it.name}
              </h3>
              <p className="text-xs text-muted-foreground flex-1 line-clamp-3">{it.description}</p>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-border">
                <span className="text-[10px] text-muted-foreground font-mono">{it.installs.toLocaleString()} installs</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => install(it)}
                  disabled={installingId === it.id}
                  aria-label={`Instalar ${it.name}`}
                  className="h-7 gap-1"
                >
                  <Download className="w-3 h-3" /> {installingId === it.id ? "..." : "Install"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
